<template>
  <section class="agent-test-page">
    <div class="test-container">
      <header class="page-header">
        <div>
          <p class="eyebrow">Live agent test</p>
          <h1>Test the full customer experience</h1>
          <p class="description">
            Validate text chat, voice, bookings, deposits, follow-ups, and website
            chatbot questions before sending real customers to the agent.
          </p>
        </div>

        <div class="agent-pill">
          <Bot :size="17" />
          <span>{{ agentDisplayName }}</span>
        </div>
      </header>

      <div v-if="error" class="error-message">{{ error }}</div>

      <div class="test-layout">
        <aside class="side-panel">
          <div class="agent-card">
            <div class="agent-avatar"><Bot :size="24" /></div>
            <div>
              <h2>{{ agentDisplayName }}</h2>
              <p>{{ activeAgent?.business_name || 'Active agent' }}</p>
            </div>
          </div>

          <div class="agent-meta-grid">
            <div class="info-box">
              <span>Agent ID</span>
              <strong>{{ agentId || 'Not selected' }}</strong>
            </div>
            <div class="info-box">
              <span>Industry</span>
              <strong>{{ activeAgent?.industry_key || activeAgent?.industry || 'Not set' }}</strong>
            </div>
            <div class="info-box">
              <span>Language</span>
              <strong>{{ activeAgent?.default_language || activeAgent?.default_lang || 'Not set' }}</strong>
            </div>
            <div class="info-box">
              <span>Session</span>
              <strong>{{ sessionId ? `#${sessionId}` : 'New test' }}</strong>
            </div>
          </div>

          <div class="voice-card">
            <div class="voice-card-header">
              <div>
                <h3>Voice simulation</h3>
                <p>Talk like a caller. The transcript appears in this test.</p>
              </div>
              <span class="voice-status" :class="{ active: isSocketConnected }">
                {{ isSocketConnected ? 'Connected' : 'Offline' }}
              </span>
            </div>

            <div class="voice-visual" :class="assistantState">
              <span v-for="bar in 5" :key="bar"></span>
            </div>

            <button
              class="voice-button"
              type="button"
              @click="startVoiceDemo"
              :disabled="!agentId || assistantState === 'connecting'"
            >
              <Mic :size="18" />
              <span>Start voice test</span>
            </button>

            <p v-if="partialTranscript" class="voice-helper">
              Listening: "{{ partialTranscript }}"
            </p>
          </div>

          <div class="quick-prompts">
            <h3>Quick tests</h3>
            <button
              v-for="prompt in quickPrompts"
              :key="prompt.label"
              type="button"
              @click="sendMessage(prompt.message)"
              :disabled="isTyping || !agentId"
            >
              <span>{{ prompt.label }}</span>
              <ChevronRight :size="16" />
            </button>
          </div>

          <button class="reset-button" type="button" @click="resetTest">
            <RefreshCcw :size="16" />
            Reset test session
          </button>
        </aside>

        <main class="chat-panel">
          <div class="chat-header">
            <div>
              <h2>Conversation preview</h2>
              <p>Text and voice messages appear together in one test thread.</p>
            </div>
            <span class="status-badge" :class="{ active: sessionId }">
              {{ sessionId ? `Session #${sessionId}` : 'No session yet' }}
            </span>
          </div>

          <div ref="chatContainer" class="chat-history">
            <div v-if="messages.length === 0" class="empty-chat">
              <div class="empty-icon"><Sparkles :size="24" /></div>
              <h3>Start with a real customer scenario</h3>
              <p>Try booking, price questions, deposit handling, or website chatbot questions.</p>
            </div>

            <div
              v-for="message in messages"
              :key="message.id"
              class="message-row"
              :class="message.role"
            >
              <div class="avatar">
                <Bot v-if="message.role === 'assistant'" :size="17" />
                <UserRound v-else :size="17" />
              </div>
              <div class="message-bubble" :class="message.role">
                <div class="message-meta">
                  <strong>{{ message.role === 'assistant' ? 'Agent' : 'You' }}</strong>
                  <span>{{ message.time }}</span>
                </div>
                <div class="message-text">{{ message.content }}</div>
              </div>
            </div>

            <div v-if="partialTranscript" class="message-row user partial">
              <div class="avatar"><UserRound :size="17" /></div>
              <div class="message-bubble user partial">
                <div class="message-meta">
                  <strong>Listening</strong>
                  <span>Live</span>
                </div>
                <div class="message-text">{{ partialTranscript }}</div>
              </div>
            </div>

            <div v-if="isTyping" class="message-row assistant">
              <div class="avatar"><Bot :size="17" /></div>
              <div class="typing-bubble"><span></span><span></span><span></span></div>
            </div>
          </div>

          <form class="composer" @submit.prevent="sendMessage()">
            <button
              type="button"
              class="mic-button"
              @click="startVoiceDemo"
              :disabled="!agentId || assistantState === 'connecting'"
              aria-label="Start voice test"
            >
              <Mic :size="20" />
            </button>
            <textarea
              v-model="userMessage"
              rows="1"
              placeholder="Speak or type your request..."
              @keydown.enter.exact.prevent="sendMessage()"
              :disabled="isTyping || !agentId"
            ></textarea>
            <button type="submit" :disabled="isTyping || !userMessage.trim() || !agentId">
              <span>{{ isTyping ? 'Sending...' : 'Send' }}</span>
              <Send :size="17" />
            </button>
          </form>
        </main>
      </div>

      <div v-if="isVoiceOverlayOpen" class="voice-overlay">
        <div class="voice-modal">
          <button class="close-button" type="button" @click="closeVoiceOverlay">×</button>
          <div class="voice-icon">
            <Volume2 v-if="assistantState === 'speaking'" :size="34" />
            <Mic v-else :size="34" />
          </div>
          <div class="overlay-title-row">
            <span class="state-dot" :class="assistantState"></span>
            <h2>{{ overlayTitle }}</h2>
          </div>
          <p>{{ overlaySubtitle }}</p>
          <div v-if="partialTranscript" class="partial-box">"{{ partialTranscript }}"</div>
          <div class="overlay-bars">
            <span v-for="bar in 5" :key="bar" :class="assistantState"></span>
          </div>
          <div class="overlay-actions">
            <button
              type="button"
              class="restart-button"
              @click="startVoiceDemo"
              :disabled="assistantState === 'connecting' || assistantState === 'thinking' || assistantState === 'speaking'"
            >
              Restart
            </button>
            <button type="button" class="stop-button" @click="closeVoiceOverlay">Close</button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  Bot,
  ChevronRight,
  Mic,
  RefreshCcw,
  Send,
  Sparkles,
  UserRound,
  Volume2
} from 'lucide-vue-next'
import Cookies from 'js-cookie'
import * as api from '@/services/api.js'

const router = useRouter()
const route = useRoute()
const API_BASE = process.env.VUE_APP_API_URL || 'https://api.nocodeagent.ai'
const WS_BASE = API_BASE.replace(/^http/, 'ws') + '/ws'

const agentId = ref(null)
const activeAgent = ref(null)
const activePersonality = ref(null)
const sessionId = ref(null)
const userMessage = ref('')
const messages = ref([])
const isTyping = ref(false)
const error = ref('')
const chatContainer = ref(null)
const assistantState = ref('idle')
const isVoiceOverlayOpen = ref(false)
const partialTranscript = ref('')
const isSocketConnected = ref(false)
const socketRef = ref(null)
const mediaRecorderRef = ref(null)
const mediaStreamRef = ref(null)
const currentAudioRef = ref(null)
const isAudioPlayingRef = ref(false)

const quickPrompts = [
  { label: 'Book an appointment', message: 'I would like to book an appointment tomorrow afternoon.' },
  { label: 'Ask about a deposit', message: 'Do I need to pay a deposit to reserve my appointment?' },
  { label: 'Website chatbot question', message: 'Can this assistant be added to my existing website as a chatbot?' },
  { label: 'No website yet', message: 'I do not have a website yet. Can you help me get one quickly?' }
]

const agentDisplayName = computed(() => {
  if (activeAgent.value?.name) return activeAgent.value.name
  if (activeAgent.value?.business_name) return activeAgent.value.business_name
  if (agentId.value) return `Agent #${agentId.value}`
  return 'No active agent'
})

const overlayTitle = computed(() => {
  if (assistantState.value === 'connecting') return 'Connecting...'
  if (assistantState.value === 'listening') return 'Listening...'
  if (assistantState.value === 'thinking') return 'Thinking...'
  if (assistantState.value === 'speaking') return 'Speaking...'
  if (assistantState.value === 'ended') return 'Voice test ended'
  if (assistantState.value === 'error') return 'Voice unavailable'
  return 'Voice test'
})

const overlaySubtitle = computed(() => {
  if (assistantState.value === 'connecting') return 'Preparing the live AI receptionist connection.'
  if (assistantState.value === 'listening') return 'Speak naturally. Ask for an appointment, pricing, a deposit, or a callback.'
  if (assistantState.value === 'thinking') return 'NoCodeAgent is understanding your request.'
  if (assistantState.value === 'speaking') return 'The AI receptionist is answering now.'
  if (assistantState.value === 'error') return 'Voice is temporarily unavailable. You can continue by typing.'
  return 'Talk to the AI receptionist.'
})

onMounted(async () => {
  const token = Cookies.get('token')
  if (!token) {
    router.push('/sign-in')
    return
  }

  resolveActiveAgentId()
  if (!agentId.value) {
    error.value = 'No active agent selected. Please select an agent first.'
    return
  }

  await loadActiveAgent()
})

onBeforeUnmount(() => cleanupVoiceDemo())

function resolveActiveAgentId() {
  const routeAgentId = Number(route.query.agent_id)
  if (Number.isFinite(routeAgentId) && routeAgentId > 0) {
    agentId.value = routeAgentId
    Cookies.set('active_agent_id', String(routeAgentId), { secure: true, sameSite: 'Strict' })
    localStorage.setItem('active_agent_id', String(routeAgentId))
    return
  }

  const cookieAgentId = Number(Cookies.get('active_agent_id'))
  const localAgentId = Number(localStorage.getItem('active_agent_id'))

  if (Number.isFinite(cookieAgentId) && cookieAgentId > 0) {
    agentId.value = cookieAgentId
    return
  }

  if (Number.isFinite(localAgentId) && localAgentId > 0) {
    agentId.value = localAgentId
    Cookies.set('active_agent_id', String(localAgentId), { secure: true, sameSite: 'Strict' })
  }
}

async function loadActiveAgent() {
  try {
    const res = await api.agents.getById(agentId.value)
    activeAgent.value = res?.agent || res?.data || res || null

    try {
      const profiles = await api.personalityProfiles.getAll(agentId.value)
      const profile = Array.isArray(profiles) && profiles.length ? profiles[0] : null
      if (profile) {
        let behavior = profile.behavior
        if (typeof behavior === 'string') {
          try {
            behavior = JSON.parse(behavior)
          } catch (_) {
            behavior = null
          }
        }
        activePersonality.value = {
          id: profile.id,
          behavior: behavior && typeof behavior === 'object' ? behavior : null
        }
      } else {
        activePersonality.value = null
      }
    } catch (profileErr) {
      console.warn('Unable to load active personality profile:', profileErr)
      activePersonality.value = null
    }
  } catch (err) {
    console.error('Unable to load active agent:', err)
    error.value = 'Unable to load the active agent.'
  }
}

function getActiveBrowserRuntimeConfig() {
  const behavior = activePersonality.value?.behavior || null
  const voice = behavior && typeof behavior.voice === 'object' ? behavior.voice : null
  const greeting = typeof behavior?.greeting === 'string' ? behavior.greeting.trim() : ''
  const voiceId =
    voice?.provider_voice_id ||
    voice?.voice_id ||
    voice?.id ||
    ''
  const language =
    voice?.language ||
    activeAgent.value?.default_language ||
    activeAgent.value?.default_lang ||
    ''

  return {
    greeting,
    voiceId,
    language
  }
}

function nowLabel() {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function pushMessage(role, content) {
  messages.value.push({ id: `${role}-${Date.now()}-${Math.random()}`, role, content, time: nowLabel() })
  scrollToBottom()
}

async function scrollToBottom() {
  await nextTick()
  if (chatContainer.value) chatContainer.value.scrollTop = chatContainer.value.scrollHeight
}

function extractAnswer(response) {
  const raw = response?.response || response?.answer_to_user || response?.message || ''
  if (!raw) return ''
  if (typeof raw !== 'string') return String(raw)

  const trimmed = raw.trim()
  if (!trimmed.startsWith('{')) return raw

  try {
    const parsed = JSON.parse(trimmed)
    return parsed.answer_to_user || parsed.response || raw
  } catch (e) {
    return raw
  }
}

async function postJson(url, payload) {
  const token = Cookies.get('token')
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload)
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(data?.error || data?.message || 'Request failed.')
  return data
}

async function getJson(url) {
  const token = Cookies.get('token')
  const response = await fetch(url, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(data?.error || data?.message || 'Request failed.')
  return data
}

async function sendMessage(forcedMessage = null) {
  const text = String(forcedMessage || userMessage.value || '').trim()
  if (!text || isTyping.value || !agentId.value) return

  error.value = ''
  userMessage.value = ''
  pushMessage('user', text)

  try {
    isTyping.value = true
    let response

    if (!sessionId.value) {
      response = await postJson(`${API_BASE}/api/chat/public/auto`, { agent_id: agentId.value, message: text })
      const newSessionId = response?.session_id || response?.sessionId || response?.payload?.session_id || null
      if (newSessionId) sessionId.value = Number(newSessionId)
    } else {
      response = await getJson(`${API_BASE}/api/chat/public/${sessionId.value}?message=${encodeURIComponent(text)}`)
    }

    pushMessage('assistant', extractAnswer(response) || 'I received your message, but no response was returned.')
  } catch (err) {
    console.error('Agent test error:', err)
    error.value = err?.message || 'Unable to send this message to the agent.'
  } finally {
    isTyping.value = false
    scrollToBottom()
  }
}

function pauseMicrophoneStream() {
  try {
    if (mediaRecorderRef.value?.state === 'recording') mediaRecorderRef.value.pause()
  } catch (err) {
    console.error('Recorder pause error:', err)
  }
}

function resumeMicrophoneStream() {
  try {
    if (mediaRecorderRef.value?.state === 'paused') mediaRecorderRef.value.resume()
  } catch (err) {
    console.error('Recorder resume error:', err)
  }
}

function cleanupVoiceDemo() {
  try {
    if (mediaRecorderRef.value && mediaRecorderRef.value.state !== 'inactive') mediaRecorderRef.value.stop()
  } catch (err) {
    console.error('MediaRecorder stop error:', err)
  }

  mediaRecorderRef.value = null
  mediaStreamRef.value?.getTracks().forEach(track => track.stop())
  mediaStreamRef.value = null

  if (currentAudioRef.value) {
    currentAudioRef.value.pause()
    currentAudioRef.value.src = ''
    currentAudioRef.value = null
  }

  try {
    socketRef.value?.close()
  } catch (err) {
    console.error('Socket close error:', err)
  }

  socketRef.value = null
  isSocketConnected.value = false
  partialTranscript.value = ''
  isAudioPlayingRef.value = false
}

function closeVoiceOverlay() {
  cleanupVoiceDemo()
  isVoiceOverlayOpen.value = false
  assistantState.value = 'idle'
}

async function playBase64Audio(mimeType, payload) {
  assistantState.value = 'speaking'
  pauseMicrophoneStream()
  isAudioPlayingRef.value = true

  try {
    currentAudioRef.value?.pause()
    const audio = new Audio(`data:${mimeType};base64,${payload}`)
    currentAudioRef.value = audio
    audio.onplay = () => pauseMicrophoneStream()
    audio.onended = () => {
      isAudioPlayingRef.value = false
      assistantState.value = 'listening'
      resumeMicrophoneStream()
    }
    audio.onerror = audio.onended
    await audio.play()
  } catch (err) {
    console.error('audio.play() failed:', err)
    isAudioPlayingRef.value = false
    assistantState.value = 'listening'
    resumeMicrophoneStream()
  }
}

async function handleSocketMessage(rawData) {
  let data
  try {
    data = JSON.parse(rawData)
  } catch (err) {
    console.error('Invalid WS message:', rawData, err)
    return
  }

  if (data.type === 'state') {
    if (data.state === 'speaking') {
      assistantState.value = 'speaking'
      pauseMicrophoneStream()
      return
    }
    if (data.state === 'listening') {
      if (isAudioPlayingRef.value) return
      assistantState.value = 'listening'
      resumeMicrophoneStream()
      return
    }
    assistantState.value = data.state
    return
  }

  if (data.type === 'partial_transcript') partialTranscript.value = data.text
  if (data.type === 'user_transcript') {
    partialTranscript.value = ''
    pushMessage('user', data.text)
    isTyping.value = true
    assistantState.value = 'thinking'
  }
  if (data.type === 'assistant_message') {
    isTyping.value = false
    pushMessage('assistant', data.text)
  }
  if (data.type === 'audio') {
    isTyping.value = false
    await playBase64Audio(data.mimeType, data.payload)
  }
  if (data.type === 'error') {
    isTyping.value = false
    assistantState.value = 'error'
    const voiceFallbackMessage =
      data.code === 'voice_provider_config_missing'
        ? 'Voice is temporarily unavailable. You can continue by typing.'
        : data.message || 'Voice is temporarily unavailable. You can continue by typing.'
    pushMessage('assistant', voiceFallbackMessage)
  }
}

async function startVoiceDemo() {
  cleanupVoiceDemo()
  error.value = ''
  isVoiceOverlayOpen.value = true
  assistantState.value = 'connecting'
  partialTranscript.value = ''

  try {
    const runtimeConfig = getActiveBrowserRuntimeConfig()
    const params = new URLSearchParams({
      mode: 'browser',
      agent_id: String(agentId.value)
    })

    if (runtimeConfig.voiceId) params.set('voice_id', runtimeConfig.voiceId)
    if (runtimeConfig.greeting) params.set('greeting', runtimeConfig.greeting)
    if (runtimeConfig.language) params.set('language', runtimeConfig.language)

    const socket = new WebSocket(`${WS_BASE}?${params.toString()}`)
    socketRef.value = socket

    socket.onopen = async () => {
      socket.send(JSON.stringify({ type: 'browser_debug_ping', message: 'hello from Vue browser', at: Date.now() }))
      isSocketConnected.value = true
      assistantState.value = 'listening'

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
        })
        mediaStreamRef.value = stream
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm'
        const recorder = new MediaRecorder(stream, { mimeType })
        mediaRecorderRef.value = recorder
        recorder.ondataavailable = async event => {
          if (!event.data || event.data.size === 0) return
          if (socketRef.value?.readyState === WebSocket.OPEN) socketRef.value.send(await event.data.arrayBuffer())
        }
        recorder.onerror = event => {
          console.error('MediaRecorder error:', event)
          assistantState.value = 'error'
        }
        recorder.start(250)
      } catch (err) {
        console.error('Microphone error:', err)
        assistantState.value = 'error'
        pushMessage('assistant', 'Microphone access was denied or unavailable. You can still type your request below.')
      }
    }
    socket.onmessage = async event => handleSocketMessage(String(event.data))
    socket.onerror = event => {
      console.error('WebSocket error:', event)
      assistantState.value = 'error'
      isSocketConnected.value = false
    }
    socket.onclose = () => {
      isSocketConnected.value = false
      if (isVoiceOverlayOpen.value && assistantState.value !== 'error' && assistantState.value !== 'ended') assistantState.value = 'idle'
    }
  } catch (err) {
    console.error('startVoiceDemo error:', err)
    assistantState.value = 'error'
    isSocketConnected.value = false
  }
}

function resetTest() {
  sessionId.value = null
  messages.value = []
  userMessage.value = ''
  error.value = ''
  partialTranscript.value = ''
  isTyping.value = false
  closeVoiceOverlay()
}
</script>

<style scoped>
.agent-test-page{min-height:calc(100vh - 64px);background:linear-gradient(135deg,#fbfaf7 0%,#eef7f1 48%,#f8fafc 100%);padding:32px 20px;color:#17211b}.test-container{max-width:1220px;margin:0 auto}.page-header{display:flex;align-items:flex-start;justify-content:space-between;gap:24px;margin-bottom:22px}.eyebrow{margin:0 0 10px;color:#0d8f62;font-size:.76rem;font-weight:800;text-transform:uppercase}h1{max-width:760px;margin:0;color:#17211b;font-size:clamp(2rem,4vw,3.6rem);font-weight:900;line-height:1}.description{max-width:760px;margin:14px 0 0;color:#66736b;line-height:1.65}.agent-pill{display:inline-flex;align-items:center;gap:9px;max-width:320px;padding:10px 14px;border:1px solid rgba(23,33,27,.1);border-radius:999px;background:#fff;font-size:.86rem;font-weight:800;white-space:nowrap;box-shadow:0 14px 30px rgba(34,45,38,.08)}.agent-pill span{overflow:hidden;text-overflow:ellipsis}.error-message{margin-bottom:18px;padding:14px 16px;border:1px solid rgba(185,28,28,.18);border-radius:8px;background:rgba(239,68,68,.08);color:#b91c1c;font-weight:800}.test-layout{display:grid;grid-template-columns:340px minmax(0,1fr);gap:20px;align-items:start}.side-panel,.chat-panel{overflow:hidden;border:1px solid rgba(23,33,27,.08);border-radius:12px;background:rgba(255,255,255,.92);box-shadow:0 24px 70px rgba(34,45,38,.12)}.side-panel{display:flex;flex-direction:column;gap:14px;padding:18px}.agent-card{display:flex;align-items:center;gap:13px;padding-bottom:16px;border-bottom:1px solid #e6ece8}.agent-avatar,.avatar,.empty-icon,.voice-icon{display:flex;align-items:center;justify-content:center}.agent-avatar{width:48px;height:48px;flex:0 0 auto;border-radius:8px;background:#17211b;color:#fff}.agent-card h2,.voice-card h3,.quick-prompts h3,.chat-header h2{margin:0;color:#17211b;font-weight:900}.agent-card h2{font-size:1.04rem}.agent-card p{margin:4px 0 0;color:#66736b;font-size:.9rem}.agent-meta-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}.info-box,.voice-card{border:1px solid #e0e8e2;border-radius:8px;background:#fbfdfb}.info-box{min-height:78px;padding:12px}.info-box span{display:block;margin-bottom:6px;color:#7a8780;font-size:.72rem;font-weight:800;text-transform:uppercase}.info-box strong{display:block;overflow:hidden;color:#17211b;font-size:.9rem;text-overflow:ellipsis}.voice-card{display:grid;gap:12px;padding:14px}.voice-card-header{display:flex;justify-content:space-between;gap:12px}.voice-card h3,.quick-prompts h3{font-size:.94rem}.voice-card p{margin:5px 0 0;color:#66736b;font-size:.82rem;line-height:1.45}.voice-status,.status-badge{height:fit-content;border-radius:999px;background:#edf2ee;color:#526159;font-size:.72rem;font-weight:900;white-space:nowrap}.voice-status{padding:6px 9px}.voice-status.active,.status-badge.active{background:rgba(13,143,98,.12);color:#0d6f4e}.voice-visual{height:42px;display:flex;align-items:center;justify-content:center;gap:6px;border:1px solid #e0e8e2;border-radius:8px;background:#fff}.voice-visual span{width:6px;height:10px;border-radius:999px;background:#9aa6a0}.voice-visual.listening span,.voice-visual.speaking span,.overlay-bars span.listening,.overlay-bars span.speaking{animation:voiceBounce 1s infinite ease-in-out;background:#0d8f62}.voice-visual.thinking span,.voice-visual.connecting span,.overlay-bars span.thinking,.overlay-bars span.connecting{animation:pulse 1s infinite ease-in-out;background:#17211b}.voice-button,.quick-prompts button,.reset-button,.composer button,.overlay-actions button{display:inline-flex;align-items:center;justify-content:center;gap:9px;border-radius:8px;font-weight:900;cursor:pointer;transition:transform .18s ease,background .18s ease,border-color .18s ease}.voice-button{min-height:44px;border:none;background:#0d8f62;color:#fff}.voice-button:disabled,.quick-prompts button:disabled,.composer button:disabled,.restart-button:disabled{cursor:not-allowed;opacity:.58}.voice-helper{margin:0;color:#526159!important;font-size:.82rem!important}.quick-prompts{display:grid;gap:10px}.quick-prompts button,.reset-button{min-height:44px;justify-content:space-between;border:1px solid #dde5df;background:#fff;color:#17211b;padding:11px 12px;text-align:left}.quick-prompts button:hover,.reset-button:hover{border-color:#b9c9c0;background:#f7fbf8}.reset-button{justify-content:center;color:#526159}.chat-panel{display:flex;flex-direction:column;min-height:720px}.chat-header{display:flex;align-items:flex-start;justify-content:space-between;gap:18px;padding:20px;border-bottom:1px solid #e6ece8;background:#fff}.chat-header h2{font-size:1.28rem}.chat-header p{max-width:680px;margin:7px 0 0;color:#66736b;line-height:1.5}.status-badge{padding:8px 12px}.chat-history{flex:1;min-height:0;overflow-y:auto;padding:22px;background:#fff}.empty-chat{min-height:420px;display:flex;flex-direction:column;align-items:center;justify-content:center;color:#66736b;text-align:center}.empty-icon{width:54px;height:54px;margin-bottom:16px;border-radius:8px;background:#17211b;color:#fff}.empty-chat h3{margin:0 0 8px;color:#17211b;font-size:1.35rem;font-weight:900}.empty-chat p{max-width:420px;margin:0;line-height:1.6}.message-row{display:flex;gap:12px;margin-bottom:16px}.message-row.user{flex-direction:row-reverse}.message-row.user .message-bubble{background:#17211b;color:#fff}.message-row.user .message-meta{color:rgba(255,255,255,.68)}.message-row.assistant .message-bubble{background:#f1f6f3;color:#17211b}.message-row.partial .message-bubble{opacity:.7;font-style:italic}.avatar{width:38px;height:38px;flex:0 0 auto;border-radius:8px;background:#edf2ee;color:#17211b}.message-bubble{max-width:min(74%,680px);border-radius:12px;padding:13px 15px;line-height:1.58}.message-meta{display:flex;justify-content:space-between;gap:16px;margin-bottom:6px;color:#66736b;font-size:.76rem}.message-text{white-space:pre-wrap;word-break:break-word}.typing-bubble{border-radius:12px;background:#f1f6f3;padding:15px}.typing-bubble span{display:inline-block;width:7px;height:7px;margin-right:6px;border-radius:50%;background:#17211b;animation:bounce 1.2s infinite ease-in-out}.composer{display:flex;gap:10px;padding:16px;border-top:1px solid #e6ece8;background:#fff}.composer textarea{flex:1;min-height:48px;max-height:160px;resize:none;border:1px solid #dce5df;border-radius:8px;padding:13px 14px;color:#17211b;font:inherit;line-height:1.45;outline:none}.composer textarea:focus{border-color:#18a16f;box-shadow:0 0 0 4px rgba(24,161,111,.12)}.composer button{min-width:104px;border:none;background:#17211b;color:#fff;padding:0 16px}.mic-button{width:48px;min-width:48px!important;padding:0!important;background:#0d8f62!important}.voice-overlay{position:fixed;inset:0;z-index:999;display:flex;align-items:center;justify-content:center;padding:20px;background:rgba(248,250,252,.82);backdrop-filter:blur(18px)}.voice-modal{position:relative;width:100%;max-width:440px;border:1px solid rgba(23,33,27,.08);border-radius:16px;background:#fff;padding:30px;text-align:center;box-shadow:0 30px 100px rgba(34,45,38,.2)}.close-button{position:absolute;top:14px;right:14px;width:36px;height:36px;border:none;border-radius:999px;background:#edf2ee;color:#17211b;font-size:1.2rem;cursor:pointer}.voice-icon{width:76px;height:76px;margin:0 auto 20px;border-radius:999px;background:#17211b;color:#fff}.overlay-title-row{display:flex;align-items:center;justify-content:center;gap:9px}.overlay-title-row h2{margin:0;color:#17211b;font-size:1.8rem;font-weight:900}.voice-modal p{margin:9px 0 0;color:#66736b;line-height:1.65}.state-dot{width:10px;height:10px;border-radius:999px;background:#9aa6a0}.state-dot.listening{background:#10b981}.state-dot.thinking{background:#f59e0b}.state-dot.speaking{background:#2563eb}.state-dot.error{background:#ef4444}.partial-box{margin-top:18px;border-radius:8px;background:#f1f6f3;padding:14px 16px;color:#526159;font-style:italic}.overlay-bars{height:64px;display:flex;align-items:end;justify-content:center;gap:8px;margin:28px auto 0}.overlay-bars span{width:10px;height:16px;border-radius:999px;background:#17211b}.overlay-actions{display:flex;gap:12px;margin-top:28px}.overlay-actions button{flex:1;min-height:46px;border:none}.restart-button{background:#17211b;color:#fff}.stop-button{background:#edf2ee;color:#17211b}@keyframes voiceBounce{0%,100%{height:10px;opacity:.5}50%{height:30px;opacity:1}}@keyframes pulse{0%,100%{opacity:.4}50%{opacity:1}}@keyframes bounce{0%,80%,100%{transform:scale(.7);opacity:.5}40%{transform:scale(1);opacity:1}}@media(max-width:980px){.test-layout{grid-template-columns:1fr}.page-header,.chat-header{flex-direction:column}.chat-panel{min-height:640px}}@media(max-width:640px){.agent-test-page{min-height:calc(100vh - 56px);padding:18px 12px}h1{font-size:1.9rem}.description{font-size:.94rem}.side-panel{padding:14px}.agent-meta-grid{grid-template-columns:1fr}.chat-header,.chat-history,.composer{padding:14px}.chat-panel{min-height:calc(100vh - 150px)}.message-bubble{max-width:86%}.composer{display:grid;grid-template-columns:48px 1fr}.composer textarea{grid-column:1/-1}.composer button[type='submit']{min-height:46px}.overlay-actions{flex-direction:column}}
</style>
